import { supabase } from '@/lib/supabase';

export interface CartItem {
  id: string
  user_id: string
  device_id: string
  quantity: number
  price: number
  created_at: string
  updated_at: string
  devices?: {
    id: string
    name: string
    model: string
    description: string
    price: number
    image_url: string
    warranty_months: number
  }
}

export const cartService = {
  /**
   * Get all cart items for the current user
   */
  async getCartItems() {
    const { data: { user }, error: authError } = await supabase.auth.getUser()
    if (authError || !user) {
      throw new Error('User not authenticated')
    }

    const { data, error } = await supabase
      .from('cart_items')
      .select(`
        *,
        devices (
          id,
          name,
          model,
          description,
          price,
          image_url,
          warranty_months
        )
      `)
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })

    if (error) {
      throw new Error(error.message || 'Failed to fetch cart items')
    }

    return data as CartItem[]
  },

  /**
   * Add item to cart or update quantity if already exists
   */
  async addToCart(deviceId: string, quantity: number = 1) {
    const { data: { user }, error: authError } = await supabase.auth.getUser()
    if (authError || !user) {
      throw new Error('Please log in to add items to your cart')
    }

    // Get device details
    const { data: device, error: deviceError } = await supabase
      .from('devices')
      .select('id, price')
      .eq('id', deviceId)
      .single()

    if (deviceError) {
      throw new Error(`Unable to find product. Error: ${deviceError.message}`)
    }
    
    if (!device) {
      throw new Error('Product not found in our catalog')
    }

    // Check if item already exists in cart
    const { data: existingItem, error: checkError } = await supabase
      .from('cart_items')
      .select('*')
      .eq('user_id', user.id)
      .eq('device_id', deviceId)
      .maybeSingle()

    if (checkError) {
      throw new Error(`Cart check failed: ${checkError.message}`)
    }

    if (existingItem) {
      // Update quantity
      const newQuantity = existingItem.quantity + quantity
      const { data, error } = await supabase
        .from('cart_items')
        .update({ quantity: newQuantity })
        .eq('id', existingItem.id)
        .select()
        .single()

      if (error) {
        throw new Error(`Failed to update cart: ${error.message}`)
      }

      return data
    } else {
      // Insert new cart item
      const { data, error } = await supabase
        .from('cart_items')
        .insert({
          user_id: user.id,
          device_id: deviceId,
          quantity,
          price: device.price
        })
        .select()
        .single()

      if (error) {
        throw new Error(`Failed to add to cart: ${error.message}`)
      }

      return data
    }
  },

  /**
   * Update cart item quantity
   */
  async updateQuantity(cartItemId: string, quantity: number) {
    if (quantity <= 0) {
      return this.removeFromCart(cartItemId)
    }

    const { data, error } = await supabase
      .from('cart_items')
      .update({ quantity })
      .eq('id', cartItemId)
      .select()
      .single()

    if (error) {
      throw new Error(error.message || 'Failed to update cart item')
    }

    return data
  },

  /**
   * Remove item from cart
   */
  async removeFromCart(cartItemId: string) {
    const { error } = await supabase
      .from('cart_items')
      .delete()
      .eq('id', cartItemId)

    if (error) {
      throw new Error(error.message || 'Failed to remove item from cart')
    }

    return true
  },

  /**
   * Clear entire cart
   */
  async clearCart() {
    const { data: { user }, error: authError } = await supabase.auth.getUser()
    if (authError || !user) {
      throw new Error('User not authenticated')
    }

    const { error } = await supabase
      .from('cart_items')
      .delete()
      .eq('user_id', user.id)

    if (error) {
      throw new Error(error.message || 'Failed to clear cart')
    }

    return true
  },

  /**
   * Calculate cart total
   */
  calculateTotal(cartItems: CartItem[]): number {
    return cartItems?.reduce((total, item) => {
      return total + (item.price * item.quantity)
    }, 0) || 0
  },

  /**
   * Calculate cart item count
   */
  getItemCount(cartItems: CartItem[]): number {
    return cartItems?.reduce((count, item) => count + item.quantity, 0) || 0
  }
}